import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
    FaUserCircle, FaCheck, FaTimes, FaCalendarPlus,
    FaEye, FaMapMarkerAlt, FaGraduationCap, FaLinkedin,
    FaGithub, FaFileAlt, FaEnvelope, FaPhone, FaBriefcase,
    FaArrowRight, FaVideo, FaCalendarAlt, FaClock, FaCheckCircle
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './TalentSourcing.css';

const TalentSourcing = () => {
    const [jobs, setJobs] = useState([]);
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Candidate Detail Modal
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // Schedule Interview Modal
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [scheduleTarget, setScheduleTarget] = useState(null); // the application row
    const [scheduleForm, setScheduleForm] = useState({
        scheduledDate: '',
        timeSlot: '',
        meetLink: '',
        notes: ''
    });

    const [filterCategory, setFilterCategory] = useState('');

    const standardizedCategories = [
        "IT & Software", "Healthcare & Medical", "Finance & Accounting", "Engineering & Manufacturing",
        "Education & Training", "Sales & Marketing", "Human Resources", "Administration & Office Support",
        "Customer Service", "Hospitality & Tourism", "Logistics & Supply Chain", "Design & Creative",
        "Media & Communications", "Legal", "Science & Research", "Construction & Trades",
        "Retail & Consumer Goods", "Data Science & Analytics", "Banking & Insurance", "Telecommunications",
        "Government & Public Sector", "NGO & Non-Profit", "Real Estate", "Automotive",
        "Agriculture & Farming", "Energy & Utilities", "Fashion & Apparel", "Food & Beverage",
        "Security & Law Enforcement", "Aviation & Aerospace", "Marine & Shipping", "Sports & Fitness",
        "Gaming & Animation", "Digital Marketing", "Content Writing", "Travel & Leisure",
        "E-commerce", "Cybersecurity", "Cloud Computing", "Quality Assurance",
        "Project Management", "Art & Photography", "Music & Entertainment", "Interior Design",
        "Social Services", "Mining & Extraction", "Event Management", "Others"
    ].sort();

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/recruiter/jobs', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const activeJobs = res.data.filter(j => j.status === 'Active' || j.status === 'Pending');
            setJobs(activeJobs);
            await fetchAllApplicants(activeJobs);
        } catch (err) {
            console.error("Error fetching jobs:", err);
            setLoading(false);
        }
    };

    const fetchAllApplicants = async (jobList) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let allApps = [];
            for (const job of (jobList || jobs)) {
                const res = await axios.get(`/api/recruiter/jobs/${job._id}/applicants`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const appsWithJob = res.data.map(app => ({
                    ...app,
                    jobTitle: job.title,
                    jobId: job._id
                }));
                allApps = [...allApps, ...appsWithJob];
            }
            setApplicants(allApps);
        } catch (err) {
            console.error("Error fetching applicants:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (applicationId, status) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.patch(`/api/recruiter/applications/${applicationId}/status`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update local state for the modal if it's open for this candidate
            if (selectedCandidate && selectedCandidate._id === applicationId) {
                setSelectedCandidate(prev => ({ ...prev, status: status }));
            }

            await fetchAllApplicants();

            const msgs = {
                Shortlisted: 'Candidate has been shortlisted!',
                Rejected: 'Application has been rejected.',
            };
            showSuccess('Status Updated', msgs[status] || 'Application status updated.');
        } catch (err) {
            showError('Update Failed', 'Could not update candidate status at this time.');
        } finally {
            setActionLoading(false);
        }
    };

    const openProfile = (application) => {
        setSelectedCandidate(application);
        setIsProfileModalOpen(true);
    };

    const openSchedule = (application) => {
        setScheduleTarget(application);
        setScheduleForm({ scheduledDate: '', timeSlot: '', meetLink: '', notes: '' });
        setIsScheduleModalOpen(true);
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        if (!scheduleTarget) return;
        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/recruiter/interviews', {
                jobId: scheduleTarget.jobId,
                candidateId: scheduleTarget.studentId?._id,
                applicationId: scheduleTarget._id,
                scheduledDate: scheduleForm.scheduledDate,
                timeSlot: scheduleForm.timeSlot,
                meetLink: scheduleForm.meetLink,
                notes: scheduleForm.notes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsScheduleModalOpen(false);
            setIsProfileModalOpen(false);
            await fetchAllApplicants();
            showSuccess('Interview Scheduled!', `Interview confirmed for ${new Date(scheduleForm.scheduledDate).toLocaleDateString()} at ${scheduleForm.timeSlot}. The candidate has been notified.`);
        } catch (err) {
            showError('Scheduling Failed', err.response?.data?.message || 'Could not schedule interview at this time.');
        } finally {
            setActionLoading(false);
        }
    };

    const applicantColumns = [
        {
            header: 'Candidate',
            key: 'studentId',
            render: (row) => (
                <div className="ts-candidate-cell" onClick={() => openProfile(row)}>
                    <div className="ts-candidate-avatar">
                        {row.studentId?.profileImage ? (
                            <img src={`${API_URL}${row.studentId.profileImage}`} alt="Avatar" />
                        ) : (
                            <FaUserCircle />
                        )}
                    </div>
                    <div className="ts-candidate-name-info">
                        <strong>{row.studentId?.name}</strong>
                        <span className="ts-candidate-email">{row.studentId?.email}</span>
                    </div>
                </div>
            )
        },
        { header: 'Applying For', key: 'jobTitle' },
        {
            header: 'Status',
            key: 'status',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase().replace(/\s+/g, '-')}`}>
                    {row.status}
                </span>
            )
        },
        {
            header: 'Applied On',
            key: 'appliedAt',
            render: (row) => new Date(row.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        },
        {
            header: 'Actions',
            key: '_id',
            render: (row) => {
                const isPending = row.status === 'Pending';
                const canSchedule = row.status === 'Shortlisted';

                return (
                    <div className="ts-action-btns">
                        <button className="ts-icon-btn ts-view" onClick={() => openProfile(row)} title="View Candidate Profile">
                            <FaEye />
                        </button>

                        {isPending && (
                            <>
                                <button className="ts-icon-btn ts-shortlist" onClick={() => handleStatusUpdate(row._id, 'Shortlisted')} title="Shortlist">
                                    <FaCheck />
                                </button>
                                <button className="ts-icon-btn ts-reject" onClick={() => handleStatusUpdate(row._id, 'Rejected')} title="Reject">
                                    <FaTimes />
                                </button>
                            </>
                        )}

                        {canSchedule && (
                            <button className="ts-icon-btn ts-schedule" onClick={() => openSchedule(row)} title="Schedule/Manage Interview">
                                <FaCalendarPlus />
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Talent Sourcing & Pipeline</h1>
                <p className="page-subtitle">Review applications and move top talent through your hiring funnel.</p>
            </div>

            <div className="premium-card">
                <div style={{ padding: '0 20px 15px', borderBottom: '1px solid #eef2f6', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaBriefcase style={{ color: '#1a3a6b' }} />
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Filter by Job Category:</span>
                        </div>
                        <select 
                            className="jl-input" 
                            style={{ maxWidth: '250px', height: '38px', padding: '0 12px' }}
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {standardizedCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <DataTable
                    title="Active Applicants"
                    columns={applicantColumns}
                    data={applicants.filter(app => {
                        if (!filterCategory) return true;
                        // Find the job this applicant applied for to check its category
                        const job = jobs.find(j => j._id === app.jobId);
                        return job?.category === filterCategory;
                    })}
                    filterOptions={[
                        { label: 'All Status', value: 'all' },
                        { label: 'Pending', value: 'Pending' },
                        { label: 'Shortlisted', value: 'Shortlisted' },
                        { label: 'Interview Scheduled', value: 'Interview Scheduled' },
                        { label: 'Rejected', value: 'Rejected' }
                    ]}
                />
            </div>

            {/* ============ CANDIDATE DETAIL MODAL ============ */}
            {isProfileModalOpen && selectedCandidate && ReactDOM.createPortal(
                <div className="jl-modal-overlay open" onClick={() => setIsProfileModalOpen(false)}>
                    <div className="jl-modal-content ts-profile-modal" onClick={e => e.stopPropagation()}>

                        {/* Hero Header */}
                        <div className="ts-profile-hero">
                            <div className="ts-hero-left">
                                <div className="ts-large-avatar">
                                    {selectedCandidate.studentId?.profileImage ? (
                                        <img src={`${API_URL}${selectedCandidate.studentId.profileImage}`} alt="Profile" />
                                    ) : (
                                        <FaUserCircle size={72} />
                                    )}
                                </div>
                                <div className="ts-hero-text">
                                    <h2>{selectedCandidate.studentId?.name || 'Unknown Candidate'}</h2>
                                    <p className="ts-hero-tagline">
                                        {selectedCandidate.studentId?.designation || selectedCandidate.studentId?.academicInfo?.education || 'Aspiring Professional'}
                                    </p>
                                    <div className="ts-hero-tags">
                                        {selectedCandidate.studentId?.location && (
                                            <span className="ts-tag"><FaMapMarkerAlt /> {selectedCandidate.studentId.location}</span>
                                        )}
                                        <span className="ts-tag"><FaBriefcase /> Applied for {selectedCandidate.jobTitle}</span>
                                    </div>
                                    <div className="ts-social-row">
                                        {selectedCandidate.studentId?.academicInfo?.linkedin && (
                                            <a href={selectedCandidate.studentId.academicInfo.linkedin} target="_blank" rel="noreferrer" className="ts-social-btn ts-li"><FaLinkedin /> LinkedIn</a>
                                        )}
                                        {selectedCandidate.studentId?.academicInfo?.github && (
                                            <a href={selectedCandidate.studentId.academicInfo.github} target="_blank" rel="noreferrer" className="ts-social-btn ts-gh"><FaGithub /> GitHub</a>
                                        )}
                                        {selectedCandidate.studentId?.resumeUrl && (
                                            <a href={`${API_URL}${selectedCandidate.studentId.resumeUrl}`} target="_blank" rel="noreferrer" className="ts-social-btn ts-resume"><FaFileAlt /> Resume</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="ts-status-box">
                                <span className={`status-badge ${selectedCandidate.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                    {selectedCandidate.status}
                                </span>
                                <p className="ts-applied-date">Applied {new Date(selectedCandidate.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                            </div>
                        </div>

                        {/* Body Content */}
                        <div className="jl-modal-body ts-profile-body">
                            <div className="ts-info-grid">

                                {/* Contact Info */}
                                <div className="ts-info-card">
                                    <h3><FaEnvelope /> Contact Information</h3>
                                    <div className="ts-info-rows">
                                        <div className="ts-info-row">
                                            <span>Email</span>
                                            <p>{selectedCandidate.studentId?.email || '—'}</p>
                                        </div>
                                        <div className="ts-info-row">
                                            <span>Phone</span>
                                            <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {selectedCandidate.studentId?.phone ? (
                                                    <><FaPhone style={{ color: '#1D4ED8', fontSize: '12px' }} /> {selectedCandidate.studentId.phone}</>
                                                ) : 'Not provided'}
                                            </p>
                                        </div>
                                        <div className="ts-info-row">
                                            <span>Location</span>
                                            <p>{selectedCandidate.studentId?.location || 'Not specified'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Academic Info */}
                                <div className="ts-info-card">
                                    <h3><FaGraduationCap /> Academic & Career</h3>
                                    <div className="ts-info-rows">
                                        <div className="ts-info-row">
                                            <span>Education</span>
                                            <p>{selectedCandidate.studentId?.academicInfo?.education || 'Not specified'}</p>
                                        </div>
                                        <div className="ts-info-row">
                                            <span>Bio</span>
                                            <p>{selectedCandidate.studentId?.bio || 'No bio provided'}</p>
                                        </div>
                                        <div className="ts-info-row">
                                            <span>Expected Salary</span>
                                            <p>{selectedCandidate.studentId?.careerPreferences?.expectedSalary || 'Open to discussion'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Skills */}
                                <div className="ts-info-card ts-full-width">
                                    <h3><FaBriefcase /> Skills & Expertise</h3>
                                    <div className="ts-skills-cloud">
                                        {selectedCandidate.studentId?.academicInfo?.skills?.length > 0 ? (
                                            selectedCandidate.studentId.academicInfo.skills.map((skill, i) => (
                                                <span key={i} className="ts-skill-tag">{skill}</span>
                                            ))
                                        ) : (
                                            <p className="ts-empty-text">No skills listed by candidate</p>
                                        )}
                                    </div>
                                </div>

                                {/* Preferred Locations */}
                                {selectedCandidate.studentId?.careerPreferences?.preferredLocations?.length > 0 && (
                                    <div className="ts-info-card ts-full-width">
                                        <h3><FaMapMarkerAlt /> Preferred Work Locations</h3>
                                        <div className="ts-skills-cloud">
                                            {selectedCandidate.studentId.careerPreferences.preferredLocations.map((loc, i) => (
                                                <span key={i} className="ts-skill-tag ts-loc-tag">{loc}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Cover Letter */}
                                {selectedCandidate.coverLetter && (
                                    <div className="ts-info-card ts-full-width">
                                        <h3><FaFileAlt /> Cover Letter</h3>
                                        <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>
                                            {selectedCandidate.coverLetter}
                                        </p>
                                    </div>
                                )}

                                {/* Recruiter Feedback */}
                                {selectedCandidate.feedback && (
                                    <div className="ts-info-card ts-full-width">
                                        <h3><FaCheckCircle /> Recruiter Feedback</h3>
                                        <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>
                                            {selectedCandidate.feedback}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="jl-modal-actions ts-profile-footer">
                            <button className="jl-btn-secondary" onClick={() => setIsProfileModalOpen(false)}>Close</button>

                            {selectedCandidate.status === 'Pending' ? (
                                <>
                                    <button
                                        className="ts-btn-reject"
                                        onClick={() => handleStatusUpdate(selectedCandidate._id, 'Rejected')}
                                        disabled={actionLoading}
                                    >
                                        <FaTimes /> Reject Candidate
                                    </button>
                                    <button
                                        className="jl-btn-gold"
                                        onClick={() => handleStatusUpdate(selectedCandidate._id, 'Shortlisted')}
                                        disabled={actionLoading}
                                    >
                                        <FaCheck /> Shortlist Candidate
                                    </button>
                                </>
                            ) : selectedCandidate.status === 'Shortlisted' ? (
                                <button
                                    className="ts-btn-schedule"
                                    onClick={() => { setIsProfileModalOpen(false); openSchedule(selectedCandidate); }}
                                    disabled={actionLoading}
                                >
                                    <FaCalendarPlus /> Schedule Interview
                                </button>
                            ) : (selectedCandidate.status === 'Interview Scheduled' || selectedCandidate.status === 'Interview Completed') ? (
                                <span style={{ fontSize: '13px', color: '#1D4ED8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FaCalendarPlus /> {selectedCandidate.status === 'Interview Completed' ? 'Interview Completed' : 'Interview Already Scheduled'}
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                , document.body)}

            {/* ============ SCHEDULE INTERVIEW MODAL ============ */}
            {isScheduleModalOpen && scheduleTarget && ReactDOM.createPortal(
                <div className="jl-modal-overlay open" onClick={() => setIsScheduleModalOpen(false)}>
                    <div className="jl-modal-content" style={{ maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
                        <div className="jl-modal-header">
                            <h2><FaCalendarAlt /> Schedule Interview</h2>
                            <button className="ts-close-btn" onClick={() => setIsScheduleModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="jl-modal-body">
                            {/* Candidate Summary */}
                            <div className="ts-schedule-candidate-card">
                                <div className="ts-schedule-avatar">
                                    {scheduleTarget.studentId?.profileImage ? (
                                        <img src={`${API_URL}${scheduleTarget.studentId.profileImage}`} alt="Avatar" />
                                    ) : (
                                        <FaUserCircle size={36} />
                                    )}
                                </div>
                                <div>
                                    <strong>{scheduleTarget.studentId?.name}</strong>
                                    <span>for <em>{scheduleTarget.jobTitle}</em></span>
                                </div>
                            </div>

                            <form onSubmit={handleScheduleSubmit} className="jl-form" style={{ marginTop: '20px' }}>
                                <div className="jl-form-group">
                                    <label className="jl-label"><FaCalendarAlt /> Interview Date</label>
                                    <input
                                        className="jl-input"
                                        type="date"
                                        min={new Date().toISOString().split('T')[0]}
                                        value={scheduleForm.scheduledDate}
                                        onChange={e => setScheduleForm({ ...scheduleForm, scheduledDate: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="jl-form-group">
                                    <label className="jl-label"><FaClock /> Time Slot</label>
                                    <input
                                        className="jl-input"
                                        type="time"
                                        value={scheduleForm.timeSlot}
                                        onChange={e => setScheduleForm({ ...scheduleForm, timeSlot: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="jl-form-group jl-full-width">
                                    <label className="jl-label"><FaVideo /> Meeting Link (Google Meet / Zoom)</label>
                                    <input
                                        className="jl-input"
                                        type="url"
                                        value={scheduleForm.meetLink}
                                        onChange={e => setScheduleForm({ ...scheduleForm, meetLink: e.target.value })}
                                        placeholder="https://meet.google.com/..."
                                    />
                                </div>
                                <div className="jl-form-group jl-full-width">
                                    <label className="jl-label">Preparation Notes (Optional)</label>
                                    <textarea
                                        className="jl-input"
                                        rows="3"
                                        value={scheduleForm.notes}
                                        onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                                        placeholder="Any context or requirements for the candidate..."
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                                <div className="jl-modal-actions">
                                    <p className="ts-helper-note">Candidate will be notified instantly via dashboard.</p>
                                    <button type="button" className="jl-btn-secondary" onClick={() => setIsScheduleModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="jl-btn-gold" disabled={actionLoading}>
                                        {actionLoading ? 'Scheduling...' : <><FaArrowRight /> Confirm Schedule</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                , document.body)}
        </div>
    );
};

export default TalentSourcing;
